import { Effect, Layer } from 'effect';

import {
  DocumentStorage,
  DocumentStorageError,
  makeDocumentStorage,
  mediaKey,
  sourceKey,
  type DocKey,
  type DocumentMedia,
  type DocumentStorageMethod,
  type StoragePort,
} from './document-storage.js';

export {
  DocumentStorage,
  DocumentStorageError,
  type DocumentMedia,
  type DocumentStorageMakeOptions,
  type DocumentStorageMethod,
} from './document-storage.js';

/** Construct the Effect requirement from runtime-neutral storage primitives. */
export const make = makeDocumentStorage;

function error(
  method: DocumentStorageMethod,
  key: DocKey,
  cause: unknown,
  mediaName?: string,
): DocumentStorageError {
  return new DocumentStorageError({
    method,
    key,
    mediaName,
    message: cause instanceof Error ? cause.message : String(cause),
    cause,
  });
}

/** Lift the established Promise StoragePort behind typed Effect operations. */
export function makeFromPort(port: StoragePort): DocumentStorage {
  const getSignedUploadUrl = port.getSignedUploadUrl?.bind(port);
  return make({
    putSource: (key, bytes) =>
      Effect.tryPromise({
        try: () => port.putSource(key, bytes),
        catch: (cause) => error('putSource', key, cause),
      }),
    getSource: (key) =>
      Effect.tryPromise({
        try: () => port.getSource(key),
        catch: (cause) => error('getSource', key, cause),
      }),
    putMedia: (key, name, bytes, contentType) =>
      Effect.tryPromise({
        try: () => port.putMedia(key, name, bytes, contentType),
        catch: (cause) => error('putMedia', key, cause, name),
      }),
    getMedia: (key, name) =>
      Effect.tryPromise({
        try: () => port.getMedia(key, name),
        catch: (cause) => error('getMedia', key, cause, name),
      }),
    getSignedUploadUrl: (key, contentType, expiresInSeconds) =>
      getSignedUploadUrl
        ? Effect.tryPromise({
            try: () => getSignedUploadUrl(key, contentType, expiresInSeconds),
            catch: (cause) => error('getSignedUploadUrl', key, cause),
          })
        : Effect.succeed(null),
  });
}

export function layerFromPort(port: StoragePort): Layer.Layer<DocumentStorage> {
  return Layer.succeed(DocumentStorage, makeFromPort(port));
}

/** Process-local document storage for contracts, tests, and ephemeral runtimes. */
export const layerMemory: Layer.Layer<DocumentStorage> = Layer.sync(DocumentStorage)(() => {
  const sources = new Map<string, Uint8Array>();
  const media = new Map<string, DocumentMedia>();

  return make({
    putSource: (key, bytes) =>
      Effect.sync(() => {
        sources.set(sourceKey(key), new Uint8Array(bytes));
      }),
    getSource: (key) =>
      Effect.sync(() => {
        const bytes = sources.get(sourceKey(key));
        return bytes === undefined ? null : new Uint8Array(bytes);
      }),
    putMedia: (key, name, bytes, contentType) =>
      Effect.sync(() => {
        media.set(mediaKey(key, name), {
          bytes: new Uint8Array(bytes),
          contentType,
        });
      }),
    getMedia: (key, name) =>
      Effect.sync(() => {
        const object = media.get(mediaKey(key, name));
        return object === undefined
          ? null
          : { bytes: new Uint8Array(object.bytes), contentType: object.contentType };
      }),
  });
});
